import { useState } from 'react'

// MultiDatePicker — pick a set of individual calendar days (item 7), possibly
// non-consecutive and across different months. value + onChange are sorted ISO
// YYYY-MM-DD strings. Month navigation reaches any month; clicking a day toggles
// it. Replaces the old from/to range inputs on the New/Edit event modals.

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

function iso(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}
function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

interface Props {
  value: string[]
  onChange: (dates: string[]) => void
  disabled?: boolean
}

export function MultiDatePicker({ value, onChange, disabled }: Props) {
  const selected = new Set(value)
  // Seed the visible month from the earliest selected date, else today.
  const [view, setView] = useState<{ y: number; m: number }>(() => {
    const seed = value.length > 0 ? [...value].sort()[0]! : todayIso()
    const [y, m] = seed.split('-').map(Number)
    return { y: y!, m: m! - 1 }
  })

  const toggle = (d: string) => {
    if (disabled) return
    const next = new Set(selected)
    if (next.has(d)) next.delete(d)
    else next.add(d)
    onChange(Array.from(next).sort())
  }
  const shiftMonth = (delta: number) => {
    setView((v) => {
      const total = v.y * 12 + v.m + delta
      return { y: Math.floor(total / 12), m: ((total % 12) + 12) % 12 }
    })
  }

  const firstWeekday = (new Date(Date.UTC(view.y, view.m, 1)).getUTCDay() + 6) % 7 // Mon=0
  const daysInMonth = new Date(Date.UTC(view.y, view.m + 1, 0)).getUTCDate()
  const today = todayIso()

  const cells: (number | null)[] = []
  for (let i = 0; i < firstWeekday; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)

  return (
    <div className="border border-line rounded-lg p-3 bg-white">
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={() => shiftMonth(-1)} disabled={disabled} className="w-7 h-7 rounded hover:bg-parchment text-ink-soft disabled:opacity-40" aria-label="Previous month">‹</button>
        <div className="font-semibold text-sm">{MONTHS[view.m]} {view.y}</div>
        <button type="button" onClick={() => shiftMonth(1)} disabled={disabled} className="w-7 h-7 rounded hover:bg-parchment text-ink-soft disabled:opacity-40" aria-label="Next month">›</button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-[10px] font-semibold uppercase text-ink-soft py-1">{w}</div>
        ))}
        {cells.map((d, i) => {
          if (d == null) return <div key={`e${i}`} />
          const ds = iso(view.y, view.m, d)
          const on = selected.has(ds)
          return (
            <button
              key={ds}
              type="button"
              onClick={() => toggle(ds)}
              disabled={disabled}
              aria-pressed={on}
              className={
                'h-8 rounded text-sm transition-colors disabled:cursor-not-allowed ' +
                (on
                  ? 'bg-green text-parchment font-semibold'
                  : 'text-ink hover:bg-parchment ' + (ds === today ? 'ring-1 ring-green/40' : ''))
              }
            >
              {d}
            </button>
          )
        })}
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-ink-soft">
        <span>
          {value.length === 0
            ? 'Click days to add them — they can span months.'
            : `${value.length} day${value.length === 1 ? '' : 's'} selected`}
        </span>
        {value.length > 0 && !disabled && (
          <button type="button" onClick={() => onChange([])} className="text-ink-soft hover:text-oxblood underline">
            Clear
          </button>
        )}
      </div>
    </div>
  )
}
